import { rem } from 'polished'
import { useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import type { PriceRoomTypeProps } from './CruisePriceRoomTypeInfo'
import CruisePriceRoomTypeInfo from './CruisePriceRoomTypeInfo'
import Heading from 'LuxKit/Typography/Heading'
import clsx from 'clsx'
import zIndex from 'styles/tools/z-index'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import type { CruiseSectionTabValues } from 'constants/cruise'
import { CruiseContentSections } from 'constants/cruise'
import TextButton from 'LuxKit/Button/TextButton'
import { HeaderHeightContext } from 'contexts/HeaderStateContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getImmersiveTripId } from 'tripPlanner/selectors'

const NavWrapper = styled(Pane)`
  position: fixed;
  width: 100vw;
  z-index: ${zIndex.stickySubHeader};
  top: 0;
  transition: transform 500ms ease-in-out;
  box-shadow: ${props => props.theme.shadow.bottom.small};
  /* Move offscreen by an extra 10px so the shadow isn't visible when header is hidden */
  transform: translateY(calc(-100% - 10px));

  &.visible {
    transform: translateY(0);
  }

`

const Inner = styled(LayoutContainer)`
  width: 100%;
  justify-content: start;
`

const TabsContainer = styled(VerticalSpacer)`
  padding-top: ${rem(16)};
`

type SECTION_TABS = Array<{
  label: string
  id: CruiseSectionTabValues
}>

interface Props extends PriceRoomTypeProps {
  visible: boolean
  title: string
  tabs: SECTION_TABS
  currentSectionTab: CruiseSectionTabValues
  handleTabsChange: (tab: CruiseSectionTabValues) => void
  onView: (ref: string) => void
}

function CruiseStickyHeader(props: Props) {
  const intl = useIntl()
  const {
    visible,
    onView,
    priceFrom,
    price,
    memberPrice,
    title,
    handleTabsChange,
    currentSectionTab,
    tabs,
    hasValidDepartures,
  } = props

  const currentTripId = useAppSelector(getImmersiveTripId)
  const { heights: { tripPlannerImmersive: tripPlannerImmersiveHeaderHeight } } = useContext(HeaderHeightContext)

  const offset = currentTripId ? tripPlannerImmersiveHeaderHeight : 0

  return (
    <NavWrapper
      className={clsx({ visible })}
      style={{ top: offset }}
      shadow="bottom-small"
      data-testid="tour-v2-sticky-header"
    >
      <Inner>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
          <TabsContainer gap={4}>

            <Heading variant="heading5" colour="normal-contrast">{title}</Heading>
            <LayoutContainer>
              <TabButtonsGroup fit="auto">
                {tabs.map((tab) => (
                  <TabButton
                    size="medium"
                    key={tab.id}
                    isActive={tab.id === currentSectionTab}
                    onClick={() => {
                      handleTabsChange(tab.id)
                      onView(tab.id)
                    }}
                  >{tab.label}
                  </TabButton>
                ))}
              </TabButtonsGroup>
            </LayoutContainer>
          </TabsContainer>
          <CSSBreakpoint min="tablet">
            <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between" tabletGap={16}>
              <CruisePriceRoomTypeInfo
                priceFrom={priceFrom}
                price={price}
                memberPrice={memberPrice}
                header
                hasValidDepartures={hasValidDepartures}
              />
              {hasValidDepartures && <TextButton kind="primary" onClick={() => onView(CruiseContentSections.DEPARTURES)}>
                {intl.formatMessage({
                  id: 'Xd_zizMcQ1go2SEZLd48',
                  defaultMessage: 'Check Availability',
                  description: 'Button label to check cruise availability in the cruise section',
                })}
              </TextButton>}
            </Group>
          </CSSBreakpoint>
        </Group>
      </Inner>
    </NavWrapper>
  )
}
export default CruiseStickyHeader
